import { Person } from "@/Common/Models/Person/Person";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { ApssClient } from "@/Patient/Api/Apss/ApssClient";
import { APSSFormInboxItem } from "@/Portfolio/Models/MyNotesInboxItem";
import { MyAPSSFormTableItem } from "@/Portfolio/Models/MyNotesInboxTableItem";
import { PortfolioStore } from "@/Portfolio/Stores/PortfolioStore";

class PortfolioApssInboxHandler extends BaseOperationHandler {
    constructor() {
        super();

        this.RegisterOperation(LocalOperations.Portfolio.APSS_Reload, this.APSS_Reload, false);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_ToggleOpenCloseSettings, this.APSS_ToggleOpenCloseSettings);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_HideSettings, this.APSS_HideSettings);

        // As of 2025/09/29, APSS counters are not working because SAFIR has shut them down du to issues with their API.
        // They are working on a fix, but for now we disable the automatic update of the counters.
        this.RegisterOperation(LocalOperations.Portfolio.APSS_UpdateApssCount, this.APSS_UpdateApssCount);

        this.RegisterOperation(LocalOperations.Portfolio.APSS_OnShowOnlyMissingRequierementChange, this.APSS_OnShowOnlyMissingRequierementChange);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_SearchSettings_ShowColumnOrdering, this.APSS_SearchSettings_ShowColumnOrdering);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_SearchSettings_HideColumnOrdering, this.APSS_SearchSettings_HideColumnOrdering);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_RowClick, this.APSS_RowClick);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_SearchChange, this.APSS_SearchChange);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_PageEvent, this.APSS_PageEvent);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_OnSettingsContainerPerPageChange, this.APSS_OnSettingsContainerPerPageChange);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_ExpandTableItem, this.APSS_ExpandTableItem);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_ConseilNumerique_Reload, this.loadConseilNumerique, false);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_ConseilNumerique_SearchChange, this.conseilNumeriqueSearchChange);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_ConseilNumerique_PageEvent, this.conseilNumeriquePageEvent);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_ConseilNumerique_SearchSettings_ChangeEmployee, this.conseilNumeriqueChangeEmployee);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_ConseilNumerique_HideColumnOrdering, this.APSS_ConseilNumerique_HideColumnOrdering);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_ConseilNumerique_ShowColumnOrdering, this.APSS_ConseilNumerique_ShowColumnOrdering);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_OnShowArchivedConseilNumerique, this.showArchivedConseilNumeriqueChange);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_SearchSettings_ChangeEmployee, this.changeEmployee);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_ActionRequired_Reload, this.apssActionRequiredReload, false);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_ActionRequired_PageEvent, this.apssActionRequiredPageEvent);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_ActionRequired_SearchChange, this.actionRequiredSearchChange);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_ActionRequired_ToggleOpenCloseSettings, this.actionRequiredOpenSettings);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_ActionRequired_SearchSettings_ChangeEmployee, this.changeActionRequiredEmployee);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_ActionRequired_OnSettingsContainerPerPageChange, this.actionRequiredOnSettingsContainerPerPageChange);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_ActionRequired_ExpandTableItem, this.actionRequiredExpandTableItem);
        this.RegisterOperation(LocalOperations.Portfolio.APSS_ActionRequired_OnShowArchived, this.showArchivedActionRequiredChange);
    }

    private APSS_OnShowOnlyMissingRequierementChange(request: LocalEvent, store: PortfolioStore): void {
        store.apssInboxModel = store.apssInboxModel.set("ShowPrerequisManquant", !store.apssInboxModel.get("ShowPrerequisManquant"));
    }

    private APSS_HideSettings(request: LocalEvent, store: PortfolioStore): void {
        store.apssInboxModel = store.apssInboxModel.set("ShowSettings", false);
        this.APSS_Reload(request, store);
    }

    private APSS_ToggleOpenCloseSettings(request: LocalEvent, store: PortfolioStore): void {
        store.apssInboxModel = store.apssInboxModel.set("ShowSettings", !store.apssInboxModel.get("ShowSettings"));
    }

    private APSS_OnSettingsContainerPerPageChange(request: LocalEvent, store: PortfolioStore): void {
        store.apssInboxModel = store.apssInboxModel
            .set("SelectedPageSizeOption", request.Get<number>("value"))
            .set("ConseilNumeriqueSelectedPageSizeOption", request.Get<number>("value"));
    }

    private APSS_PageEvent(request: LocalEvent, store: PortfolioStore): void {
        var value = request.Get<number>("Page");
        store.apssInboxModel = store.apssInboxModel.set("CurrentPage", value);
        this.APSS_Reload(request, store);
    }

    private APSS_SearchChange(request: LocalEvent, store: PortfolioStore): void {
        var value = request.Get<string>("value");
        store.apssInboxModel = store.apssInboxModel.set("GlobalPortfolioSearchText", value);
        this.APSS_Reload(request, store);
    }

    private APSS_Reload(request: LocalEvent, store: PortfolioStore): void {
        // Throtlled
        if (store.timeoutReferenceAPSS !== -1) {
            clearTimeout(store.timeoutReferenceAPSS);
            store.timeoutReferenceAPSS = -1;
        }

        store.apssInboxModel.set("TableData", Immutable.List([]));
        store.timeoutReferenceAPSS = window.setTimeout(async () => {
            try {
                const response = await ApssClient.post(
                    ApssClient.getRecentRequests(
                        portfolioStore.apssInboxModel.get("FilterType"),
                        portfolioStore.apssInboxModel.get("SelectedEmployee")?.EmployeeModel,
                        portfolioStore.apssInboxModel.get("CurrentPage"),
                        portfolioStore.apssInboxModel.get("SelectedPageSizeOption"),
                        portfolioStore.apssInboxModel.get("ShowPrerequisManquant"),
                        portfolioStore.apssInboxModel.get("StatusFilter"),
                        portfolioStore.apssInboxModel.get("GlobalPortfolioSearchText")
                    )
                );

                const requetesRaw = response.Requetes;

                if (requetesRaw != null) {
                    const decodedObject = MyAPSSFormTableItem.DecodeHtmlCaracters<APSSFormInboxItem[]>(requetesRaw);
                    const expendedItem = portfolioStore.apssInboxModel.get("ExpandPrerequis");
                    store.apssInboxModel = store.apssInboxModel.set(
                        "TableData",
                        decodedObject
                            .map(x => {
                                const tableItem = new MyAPSSFormTableItem(x);
                                if (expendedItem != null && tableItem.Model.ReqReg_Id == expendedItem)
                                    tableItem.ExpandPrerequis = true;
                                return tableItem;
                            })
                            .toImmutable()
                    );
                }

                const totalResults = response?.ReqDisplayTotal as number ?? 0; // ReqTotal
                if (totalResults !== 0)
                    store.apssInboxModel = store.apssInboxModel.set("TotalQueryRows", totalResults);

                store.EmitChangeToListeners();
            }
            catch (error) {
                console.error("error reloading APSS", error);
            }
        }, 250);
    }

    private loadConseilNumerique(request: LocalEvent, store: PortfolioStore): void {
        if (store.timeoutReferenceAPSS !== -1) {
            clearTimeout(store.timeoutReferenceAPSS);
            store.timeoutReferenceAPSS = -1;
        }

        store.timeoutReferenceAPSS = window.setTimeout(async () => {
            try {
                const response = await ApssClient.post(ApssClient.fetchAllConseilNumeriqueForms(
                    portfolioStore.apssInboxModel.get("ConseilNumeriqueFilterType"),
                    portfolioStore.apssInboxModel.get("ConseilNumeriqueSelectedEmployee")?.EmployeeModel,
                    portfolioStore.apssInboxModel.get("ConseilNumeriqueCurrentPage"),
                    portfolioStore.apssInboxModel.get("ConseilNumeriqueSelectedPageSizeOption"),
                    portfolioStore.apssInboxModel.get("GlobalPortfolioSearchText"),
                    portfolioStore.apssInboxModel.get("ShowArchivedConseilNumerique")
                ));

                if (!response?.Requetes)
                    return;

                const requetesRaw = response.Requetes;

                if (requetesRaw != null) {
                    const decodedObject = MyAPSSFormTableItem.DecodeHtmlCaracters<APSSFormInboxItem[]>(requetesRaw);
                    const expendedItem = portfolioStore.apssInboxModel.get("ExpandPrerequis");

                    store.apssInboxModel = store.apssInboxModel.set(
                        "ConseilNumeriqueTableData",
                        decodedObject
                            .map(x => {
                                const tableItem = new MyAPSSFormTableItem(x);
                                if (expendedItem != null && tableItem.Model.ReqReg_Id == expendedItem)
                                    tableItem.ExpandPrerequis = true;
                                return tableItem;
                            })
                            .toImmutable()
                    ).set("ShowSettings", false);

                    const totalResults = response?.ReqDisplayTotal as number ?? 0; // ReqTotal
                    if (totalResults !== 0)
                        store.apssInboxModel = store.apssInboxModel.set("ConseilNumeriqueTotalQueryRows", totalResults);

                    if (cacheStore.ClinicSettings.EnableConseilNumerique && cacheStore.ClinicSettings.EnableAPSSCounter) {
                        const countConseilNumeriqueResponse = await ApssClient.post(ApssClient.getAPSSRequestCount(true, false));
                        const conseilNumeriqueCount = countConseilNumeriqueResponse?.ReqDisplayTotal as number ?? 0;
                        if (conseilNumeriqueCount !== 0) {
                            store.Model = store.Model.set("ApssConseilNumeriqueCount", conseilNumeriqueCount);

                            store.PortfolioActions.LoadPortfolioCount();
                        }
                    }

                    store.EmitChangeToListeners();
                }
            }
            catch (error) {
                console.error("error reloading Conseil Numerique forms", error);
            }
        });
    }

    private conseilNumeriqueSearchChange(request: LocalEvent, store: PortfolioStore): void {
        const value = request.Get<string>("value");
        store.apssInboxModel = store.apssInboxModel.set("GlobalPortfolioSearchText", value);
        this.loadConseilNumerique(request, store);
    }

    private APSS_UpdateApssCount(request: LocalEvent, store: PortfolioStore): void {
        if (!cacheStore.ClinicSettings.EnableAPSSCounter)
            return;

        // throttled
        if (store.timeoutReferenceAPSS !== -1) {
            clearTimeout(store.timeoutReferenceAPSS);
            store.timeoutReferenceAPSS = -1;
        }

        store.timeoutReferenceAPSS = window.setTimeout(async () => {
            try {
                const countRequestResponse = await ApssClient.post(ApssClient.getAPSSRequestCount(false, false));
                const recentCount = countRequestResponse?.ReqDisplayTotal as number ?? 0;
                if (recentCount !== 0)
                    store.Model = store.Model.set("ApssRecentCount", recentCount);

                const countConseilNumeriqueResponse = await ApssClient.post(ApssClient.getAPSSRequestCount(true, false));
                const conseilNumeriqueCount = countConseilNumeriqueResponse?.ReqDisplayTotal as number ?? 0;
                if (conseilNumeriqueCount != null)
                    store.Model = store.Model.set("ApssConseilNumeriqueCount", conseilNumeriqueCount);

                const countActionRequiredResponse = await ApssClient.post(ApssClient.getAPSSRequestCount(false, true));
                const actionRequiredCount = countActionRequiredResponse?.ReqDisplayTotal as number ?? 0;
                if (actionRequiredCount != null)
                    store.Model = store.Model.set("ApssActionRequiredCount", actionRequiredCount);

                store.EmitChangeToListeners();
            }
            catch (error) {
                console.error("error updating APSS count", error);
            }
        }, 250);
    }

    private conseilNumeriquePageEvent(request: LocalEvent, store: PortfolioStore): void {
        const value = request.Get<number>("Page");
        store.apssInboxModel = store.apssInboxModel.set("ConseilNumeriqueCurrentPage", value);
        this.loadConseilNumerique(request, store);
    }

    private conseilNumeriqueChangeEmployee(request: LocalEvent, store: PortfolioStore): void {
        const value = request.Get<Person>("value");
        store.apssInboxModel = store.apssInboxModel.set("ConseilNumeriqueSelectedEmployee", value);
    }

    private APSS_ConseilNumerique_ShowColumnOrdering(request: LocalEvent, store: PortfolioStore): void {
        store.apssInboxModel = store.apssInboxModel.set("ConseilNumeriqueColumnOrderingPopupOpen", true);
    }

    private APSS_ConseilNumerique_HideColumnOrdering(request: LocalEvent, store: PortfolioStore): void {
        store.apssInboxModel = store.apssInboxModel.set("ConseilNumeriqueColumnOrderingPopupOpen", false);
    }

    private showArchivedConseilNumeriqueChange(request: LocalEvent, store: PortfolioStore): void {
        store.apssInboxModel = store.apssInboxModel.set("ShowArchivedConseilNumerique", !store.apssInboxModel.get("ShowArchivedConseilNumerique"));
    }

    private APSS_SearchSettings_ShowColumnOrdering(request: LocalEvent, store: PortfolioStore): void {
        store.apssInboxModel = store.apssInboxModel.set("ColumnOrderingPopupOpen", true);
    }

    private APSS_SearchSettings_HideColumnOrdering(request: LocalEvent, store: PortfolioStore): void {
        store.apssInboxModel = store.apssInboxModel.set("ColumnOrderingPopupOpen", false);
    }

    private APSS_RowClick(request: LocalEvent): void {
        var data = request.Get<APSSFormInboxItem>("rowData");

        ApssClient.openForm(data.ReqReg_Id, null); // TODO: temporary solution, same historical Id as the one used to generate de list of forms
    }

    private APSS_ExpandTableItem(request: LocalEvent): void {
        let id = request.Get<string>("id");
        let data = request.Get<Immutable.List<MyAPSSFormTableItem>>("data");

        portfolioStore.apssInboxModel = portfolioStore.apssInboxModel.set("ExpandPrerequis", id);
        portfolioStore.apssInboxModel = portfolioStore.apssInboxModel.set("TableData", data);
        portfolioStore.EmitChangeToListeners();
    }

    private changeEmployee(request: LocalEvent, store: PortfolioStore): void {
        const value = request.Get<Person>("value");
        store.apssInboxModel = store.apssInboxModel.set("SelectedEmployee", value);
    }

    private apssActionRequiredReload(request: LocalEvent, store: PortfolioStore): void {
        if (store.timeoutReferenceAPSS !== -1) {
            clearTimeout(store.timeoutReferenceAPSS);
            store.timeoutReferenceAPSS = -1;
        }

        portfolioStore.apssInboxModel = portfolioStore.apssInboxModel.set("ActionRequiredShowSettings", false);

        store.timeoutReferenceAPSS = globalThis.setTimeout(async () => {
            try {
                const response = await ApssClient.post(ApssClient.getRequestWithAdvancedFilters(
                    portfolioStore.apssInboxModel.get("FilterType"),
                    portfolioStore.apssInboxModel.get("ActionRequiredSelectedEmployee")?.EmployeeModel,
                    portfolioStore.apssInboxModel.get("ActionRequiredCurrentPage"),
                    portfolioStore.apssInboxModel.get("ActionRequiredSelectedPageSizeOption"),
                    portfolioStore.apssInboxModel.get("ActionRequiredSearchText"),
                    portfolioStore.apssInboxModel.get("ShowArchivedActionRequired")
                ));

                const requetesRaw = response.Requetes;
                if (requetesRaw) {
                    const decodedObject = MyAPSSFormTableItem.DecodeHtmlCaracters<APSSFormInboxItem[]>(requetesRaw);
                    const expendedItem = portfolioStore.apssInboxModel.get("ExpandPrerequis");

                    store.apssInboxModel = store.apssInboxModel.set(
                        "ActionRequiredTableData",
                        decodedObject
                            .map(x => {
                                const tableItem = new MyAPSSFormTableItem(x);
                                if (expendedItem != null && tableItem.Model.ReqReg_Id == expendedItem)
                                    tableItem.ExpandPrerequis = true;

                                return tableItem;
                            })
                            .toImmutable()
                    ).set("ShowSettings", false);

                    const totalResults = response?.ReqDisplayTotal as number ?? 0; // ReqTotal
                    if (totalResults !== 0)
                        store.apssInboxModel = store.apssInboxModel.set("ActionRequiredTotalQueryRows", totalResults);

                    const countActionRequiredResponse = await ApssClient.post(ApssClient.getAPSSRequestCount(false, true));
                    const actionRequiredCount = countActionRequiredResponse?.ReqDisplayTotal as number ?? 0;
                    if (actionRequiredCount !== 0) {
                        store.Model = store.Model.set("ApssActionRequiredCount", actionRequiredCount);

                        store.PortfolioActions.LoadPortfolioCount();
                    }
                    store.EmitChangeToListeners();
                }
            }
            catch (error) {
                console.error("error reloading APSS Action Required forms", error);
            }
        }, 250);
    }

    private apssActionRequiredPageEvent(request: LocalEvent, store: PortfolioStore): void {
        const value = request.Get<number>("Page");
        store.apssInboxModel = store.apssInboxModel.set("ActionRequiredCurrentPage", value);
        this.apssActionRequiredReload(request, store);
    }

    private actionRequiredSearchChange(request: LocalEvent, store: PortfolioStore): void {
        const value = request.Get<string>("value");
        store.apssInboxModel = store.apssInboxModel.set("ActionRequiredSearchText", value);
        this.apssActionRequiredReload(request, store);
    }

    private actionRequiredOpenSettings(request: LocalEvent, store: PortfolioStore): void {
        store.apssInboxModel = store.apssInboxModel.set("ActionRequiredShowSettings", !store.apssInboxModel.get("ActionRequiredShowSettings"));
    }

    private changeActionRequiredEmployee(request: LocalEvent, store: PortfolioStore): void {
        const value = request.Get<Person>("value");
        store.apssInboxModel = store.apssInboxModel.set("ActionRequiredSelectedEmployee", value);
    }

    private actionRequiredOnSettingsContainerPerPageChange(request: LocalEvent, store: PortfolioStore): void {
        store.apssInboxModel = store.apssInboxModel.set("ActionRequiredSelectedPageSizeOption", request.Get<number>("value"));
    }

    private actionRequiredExpandTableItem(request: LocalEvent): void {
        let id = request.Get<string>("id");
        let data = request.Get<Immutable.List<MyAPSSFormTableItem>>("data");

        portfolioStore.apssInboxModel = portfolioStore.apssInboxModel.set("ActionRequiredExpandPrerequis", id);
        portfolioStore.apssInboxModel = portfolioStore.apssInboxModel.set("ActionRequiredTableData", data);
    }

    private showArchivedActionRequiredChange(request: LocalEvent, store: PortfolioStore): void {
        store.apssInboxModel = store.apssInboxModel.set("ShowArchivedActionRequired", !store.apssInboxModel.get("ShowArchivedActionRequired"));
    }
}

export { PortfolioApssInboxHandler };
